import { DeleteTaskButton } from '../accounts/DeleteTaskButton';
import React, { useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, LinearProgress, Link, Tab, TabList, Tabs, Typography } from '@mui/joy';
import type { ColorPaletteProp } from '@mui/joy';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ResponsiveTable } from '../../../components/common/ResponsiveTable';
import type { ResponsiveColumn } from '../../../components/common/ResponsiveTable';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import { AdminQueryError } from '../../../components/admin/AdminQueryError';
import { getApiErrorMessage } from '../../../lib/apiError';
import { AdminOnboardingAPI } from '../../../lib/apiAdmin';
import type { StaffTask } from '../../../types/admin';

type Scope = 'me' | 'none' | 'all';

const SCOPE_LABEL: Record<Scope, string> = {
  me: 'Mine',
  none: 'Unassigned',
  all: 'Everyone',
};

/** How a task got here. Automatic ones carry the rule that opened them. */
const RULE_LABELS: Record<string, string> = {
  new_customer: 'New customer',
  cx_trial_setup: 'Trial setup',
  dev_ticket_shipped: 'Fix shipped',
  nps_detractor: 'Unhappy NPS',
};

const dueMeta = (task: StaffTask): { label: string; color: ColorPaletteProp } | null => {
  if (!task.due_at) return null;
  const due = new Date(task.due_at);
  const days = Math.round((due.getTime() - Date.now()) / 86_400_000);
  if (task.is_overdue) return { label: `${Math.abs(days)}d overdue`, color: 'danger' };
  if (days <= 0) return { label: 'Today', color: 'warning' };
  if (days === 1) return { label: 'Tomorrow', color: 'warning' };
  return { label: due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), color: 'neutral' };
};

/**
 * What Customer Success actually has to do, as the first thing the tab shows.
 *
 * Every rule in the onboarding service opens tasks — nudges, welcome calls,
 * "tell the customer it shipped" — and until now the only way to find them was
 * to open customers one at a time and look. A queue is the difference between
 * work that is tracked and work that is remembered.
 */
export const AdminTaskQueue: React.FC<{ onOpenCustomer?: (id: string) => void }> = ({
  onOpenCustomer,
}) => {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope>('me');
  const [showDone, setShowDone] = useState(false);

  const tasksQuery = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['admin', 'task-queue', { scope, showDone }],
    queryFn: ({pageParam}) =>
      AdminOnboardingAPI.listTasks({
        page: pageParam,
        assignee: scope === 'all' ? undefined : scope,
        state: showDone ? 'all' : 'open',
        page_size: 50,
      }),
    getNextPageParam: (last, pages) => last.next ? pages.length + 1 : undefined,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'task-queue'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'customer'] });
    queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
    queryClient.invalidateQueries({ queryKey: ['admin-org'] });
  };

  const completeMutation = useMutation({
    mutationFn: (id: string) => AdminOnboardingAPI.completeTask(id),
    onSuccess: invalidate,
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => AdminOnboardingAPI.reopenTask(id),
    onSuccess: invalidate,
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, assignee }: { id: string; assignee: number | null }) =>
      AdminOnboardingAPI.updateTask(id, { assignee }),
    onSuccess: invalidate,
  });

  const rows = tasksQuery.data?.pages.flatMap(page => page.results) ?? [];
  const overdue = rows.filter((task) => task.is_overdue && task.is_open).length;

  const columns: ResponsiveColumn<StaffTask>[] = [
    {
      key: 'title',
      header: 'Task',
      mobileVariant: 'header',
      mobileLabel: 'Task',
      sortValue: (task) => task.title,
      render: (task) => (
        <Box>
          <Typography
            level="title-sm"
            sx={{ textDecoration: task.is_open ? 'none' : 'line-through' }}
          >
            {task.title}
          </Typography>
          {task.notes && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
              {task.notes.split('\n')[0]}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'organization',
      header: 'Account / Prospect',
      width: '20%',
      sortValue: (task) => task.lead_name || task.organization_name,
      render: (task) =>
        task.customer && onOpenCustomer ? (
          <Typography
            component="button" type="button"
            level="body-sm"
            sx={{ cursor: 'pointer', textDecoration: 'underline dotted', border: 0, bgcolor: 'transparent', textAlign: 'left', p: 0 }}
            onClick={() => onOpenCustomer(task.customer)}
          >
            {task.organization_name}
          </Typography>
        ) : task.lead && task.lead_name ? (
          <Link href={`/manage/crm?contact=${task.lead}`} level="body-sm">{task.lead_name}</Link>
        ) : (
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            {task.organization_name || '—'}
          </Typography>
        ),
    },
    {
      key: 'due',
      header: 'Due',
      width: '13%',
      // Sorts on the raw date; the chip only ever shows how it reads.
      sortValue: (task) => (task.due_at ? new Date(task.due_at) : null),
      render: (task) => {
        const meta = dueMeta(task);
        return meta ? (
          <Chip size="sm" variant="soft" color={meta.color}>
            {meta.label}
          </Chip>
        ) : (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            —
          </Typography>
        );
      },
    },
    {
      key: 'why',
      header: 'Why',
      width: '13%',
      sortValue: (task) => task.created_by_rule,
      render: (task) =>
        task.created_by_rule ? (
          <Chip size="sm" variant="outlined" color="neutral">
            {RULE_LABELS[task.created_by_rule] ?? task.created_by_rule.replace(/_/g, ' ')}
          </Chip>
        ) : (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            By hand
          </Typography>
        ),
    },
    {
      key: 'assignee',
      header: 'Owner',
      width: '18%',
      // Full width on a card: a Select in a label/value row is squeezed to a
      // few characters and becomes unusable on a phone.
      mobileVariant: 'body',
      mobileLabel: 'Owner',
      sortValue: (task) => task.assignee_name,
      render: (task) => (
        <StaffPicker
          value={task.assignee}
          onChange={(assignee) => assignMutation.mutate({ id: task.id, assignee })}
          placeholder="Unassigned"
        />
      ),
    },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tabs value={scope} onChange={(_, value) => setScope(value as Scope)} sx={{ bgcolor: 'transparent' }}>
          <TabList size="sm">
            {(Object.keys(SCOPE_LABEL) as Scope[]).map((key) => (
              <Tab key={key} value={key}>
                {SCOPE_LABEL[key]}
              </Tab>
            ))}
          </TabList>
        </Tabs>

        {overdue > 0 && (
          <Chip size="sm" variant="soft" color="danger">
            {overdue} overdue in loaded tasks
          </Chip>
        )}

        <Box sx={{ flex: 1 }} />
        <Button size="sm" variant="plain" color="neutral" onClick={() => setShowDone((prev) => !prev)}>
          {showDone ? 'Open only' : 'Show completed'}
        </Button>
        <IconButton size="sm" variant="outlined" color="neutral" aria-label="Refresh" onClick={invalidate}>
          <ArrowsClockwise size={16} />
        </IconButton>
      </Box>

      {tasksQuery.isFetching && <LinearProgress size="sm" />}
      <AdminQueryError error={tasksQuery.error} retry={() => tasksQuery.isFetchNextPageError ? tasksQuery.fetchNextPage() : tasksQuery.refetch()} message="Could not load the task queue." />
      {(completeMutation.error || reopenMutation.error || assignMutation.error) && <Alert color="danger">{getApiErrorMessage(completeMutation.error || reopenMutation.error || assignMutation.error, 'The task could not be updated.')}</Alert>}

      <ResponsiveTable<StaffTask>
        rows={rows}
        getRowKey={(task) => task.id}
        columns={columns}
        pagination={{ pageSize: 25, itemNoun: 'tasks' }}
        rowActions={(task) => (
          <Box sx={{ display: 'flex', gap: .5 }}><Button
            size="sm"
            variant="plain"
            color="neutral"
            loading={
              (completeMutation.isPending && completeMutation.variables === task.id) ||
              (reopenMutation.isPending && reopenMutation.variables === task.id)
            }
            onClick={() =>
              task.is_open ? completeMutation.mutate(task.id) : reopenMutation.mutate(task.id)
            }
          >
            {task.is_open ? 'Done' : 'Reopen'}
          </Button><DeleteTaskButton task={task} /></Box>
        )}
        emptyState={
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            {tasksQuery.isError ? 'Task list unavailable.' : tasksQuery.isPending ? 'Loading tasks…' : scope === 'me'
              ? 'Nothing assigned to you.'
              : scope === 'none'
                ? 'Everything has an owner.'
                : 'No open tasks.'}
          </Typography>
        }
      />
      {tasksQuery.hasNextPage && <Button variant="outlined" loading={tasksQuery.isFetchingNextPage} onClick={() => tasksQuery.fetchNextPage()}>Load more tasks ({rows.length} of {tasksQuery.data?.pages[0].count})</Button>}
    </Box>
  );
};

export default AdminTaskQueue;
